import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SocialProof from "@/components/SocialProof";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Wind, Droplets, TrendingUp, Megaphone, Phone, CalendarCheck, Star, Target, HardHat, Zap, Trees, Paintbrush, Warehouse, Bug } from "lucide-react";

const tradeBenefits = (noun) => ([
  { icon: Megaphone, label: "10,000-home reach", body: `Your ${noun} offer mailed to 10,000 nearby homes.` },
  { icon: Phone, label: "More jobs booked", body: "Turn local demand into booked, paying jobs." },
  { icon: CalendarCheck, label: "Automated follow-up", body: "Your CRM captures and books every lead for you." },
  { icon: Star, label: "Trusted locally", body: "Featured in the SAM directory neighbors trust." },
]);

const LANDINGS = {
  hvac: {
    icon: Wind,
    eyebrow: "HVAC · Heating & Air Conditioning",
    h1: "Get more HVAC service calls & installs",
    sub: "Saving America Money puts your heating & cooling business in front of 10,000 local homes with our 9×12 co-op postcard, a trusted directory listing, and an AI-powered CRM that books the jobs for you.",
    cta: "Get more HVAC customers",
    companyLabel: "HVAC business name",
    timeLabel: "When do you want to start?",
    needsLabel: "Which jobs do you want more of?",
    needs: ["AC repair", "AC installation", "Heating & furnace", "New system install", "Maintenance plans", "Indoor air quality"],
    benefits: [
      { icon: Megaphone, label: "10,000-home reach", body: "Your seasonal HVAC offer mailed to 10,000 nearby homes." },
      { icon: Phone, label: "More service calls", body: "Turn peak-season demand into booked repairs and installs." },
      { icon: CalendarCheck, label: "Automated follow-up", body: "Your CRM captures and books every lead automatically." },
      { icon: Star, label: "Trusted locally", body: "Featured in the SAM directory neighbors already trust." },
    ],
    showCompany: true,
  },
  plumber: {
    icon: Droplets,
    eyebrow: "Plumbing Services",
    h1: "Get more plumbing jobs & emergency calls",
    sub: "Saving America Money helps your plumbing company win more work — mailed to 10,000 local homes, listed in our trusted directory, and backed by an AI-powered CRM that turns calls into booked jobs.",
    cta: "Get more plumbing customers",
    companyLabel: "Plumbing business name",
    timeLabel: "When do you want to start?",
    needsLabel: "Which jobs do you want more of?",
    needs: ["Leak & pipe repair", "Drain cleaning", "Water heaters", "Repiping", "Emergency calls", "Fixture install"],
    benefits: [
      { icon: Megaphone, label: "10,000-home reach", body: "Your plumbing offer mailed to 10,000 homes nearby." },
      { icon: Phone, label: "Emergency-ready", body: "Be the first call when a pipe bursts or a drain clogs." },
      { icon: CalendarCheck, label: "Automated follow-up", body: "Your CRM captures and books every job for you." },
      { icon: Star, label: "Trusted locally", body: "Featured in the SAM directory neighbors trust." },
    ],
    showCompany: true,
  },
  roofer: {
    icon: HardHat,
    eyebrow: "Roofing Contractors",
    h1: "Get more roofing jobs & inspections",
    sub: "Saving America Money puts your roofing company in front of 10,000 local homes with our 9×12 co-op postcard, a trusted directory listing, and an AI-powered CRM that books estimates and jobs for you.",
    cta: "Get more roofing customers",
    companyLabel: "Roofing business name",
    timeLabel: "When do you want to start?",
    needsLabel: "Which jobs do you want more of?",
    needs: ["Roof repair", "Roof replacement", "Storm damage", "Inspections", "Gutters", "Emergency leaks"],
    benefits: tradeBenefits("roofing"),
    showCompany: true,
  },
  electrician: {
    icon: Zap,
    eyebrow: "Electricians",
    h1: "Get more electrical jobs & service calls",
    sub: "Saving America Money helps your electrical business win more work — mailed to 10,000 local homes, listed in our trusted directory, and backed by an AI-powered CRM that turns calls into booked jobs.",
    cta: "Get more electrical customers",
    companyLabel: "Electrical business name",
    timeLabel: "When do you want to start?",
    needsLabel: "Which jobs do you want more of?",
    needs: ["Repairs & troubleshooting", "Panel upgrades", "EV chargers", "Lighting install", "Rewiring", "Emergency calls"],
    benefits: tradeBenefits("electrical"),
    showCompany: true,
  },
  landscaper: {
    icon: Trees,
    eyebrow: "Landscaping & Lawn Care",
    h1: "Get more landscaping & lawn care clients",
    sub: "Saving America Money puts your landscaping business in front of 10,000 local homes with our 9×12 co-op postcard, a trusted directory listing, and an AI-powered CRM that books the clients for you.",
    cta: "Get more landscaping customers",
    companyLabel: "Landscaping business name",
    timeLabel: "When do you want to start?",
    needsLabel: "Which services do you want more of?",
    needs: ["Lawn care", "Landscape design", "Hardscaping", "Irrigation", "Tree & shrub care", "Seasonal cleanup"],
    benefits: tradeBenefits("landscaping"),
    showCompany: true,
  },
  painter: {
    icon: Paintbrush,
    eyebrow: "Painting Contractors",
    h1: "Get more painting jobs & estimates",
    sub: "Saving America Money puts your painting company in front of 10,000 local homes with our 9×12 co-op postcard, a trusted directory listing, and an AI-powered CRM that books estimates for you.",
    cta: "Get more painting customers",
    companyLabel: "Painting business name",
    timeLabel: "When do you want to start?",
    needsLabel: "Which jobs do you want more of?",
    needs: ["Interior painting", "Exterior painting", "Cabinet refinishing", "Drywall & repair", "Deck & fence staining", "Commercial painting"],
    benefits: tradeBenefits("painting"),
    showCompany: true,
  },
  garage_door: {
    icon: Warehouse,
    eyebrow: "Garage Door Services",
    h1: "Get more garage door repairs & installs",
    sub: "Saving America Money helps your garage door business win more work — mailed to 10,000 local homes, listed in our trusted directory, and backed by an AI-powered CRM that turns calls into booked jobs.",
    cta: "Get more garage door customers",
    companyLabel: "Garage door business name",
    timeLabel: "When do you want to start?",
    needsLabel: "Which jobs do you want more of?",
    needs: ["Spring repair", "Opener repair", "New door install", "Off-track doors", "Maintenance & tune-up", "Emergency service"],
    benefits: tradeBenefits("garage door"),
    showCompany: true,
  },
  pest_control: {
    icon: Bug,
    eyebrow: "Pest Control",
    h1: "Get more pest control jobs & recurring plans",
    sub: "Saving America Money puts your pest control business in front of 10,000 local homes with our 9×12 co-op postcard, a trusted directory listing, and an AI-powered CRM that books the jobs for you.",
    cta: "Get more pest control customers",
    companyLabel: "Pest control business name",
    timeLabel: "When do you want to start?",
    needsLabel: "Which services do you want more of?",
    needs: ["General pest", "Termite control", "Rodent control", "Mosquito & tick", "Bed bugs", "Recurring plans"],
    benefits: tradeBenefits("pest control"),
    showCompany: true,
  },
  business: {
    icon: TrendingUp,
    eyebrow: "For Realtors, Agents & Local Businesses",
    h1: "Do you need more leads or sales for your business or real estate company?",
    sub: "Saving America Money helps realtors, agents, and local businesses grow — reaching 10,000 homes on our 9×12 postcard, a trusted directory listing, and an AI-powered CRM that captures leads and turns them into sales.",
    cta: "Get more leads & sales",
    companyLabel: "Company / business name",
    timeLabel: "When do you want to start?",
    needsLabel: "What are your goals?",
    needs: ["More leads", "More sales", "Brand awareness", "More reviews", "Book appointments", "Repeat customers"],
    benefits: [
      { icon: Target, label: "Qualified local leads", body: "Reach ready-to-buy customers in your ZIP codes." },
      { icon: Megaphone, label: "Postcard + directory", body: "10,000-home mailers plus a trusted directory listing." },
      { icon: CalendarCheck, label: "AI-powered CRM", body: "Capture, follow up, and book — all automated." },
      { icon: TrendingUp, label: "Built for sales", body: "Turn local attention into real revenue." },
    ],
    showCompany: true,
    showBizType: true,
    showWebsite: true,
    showBudget: true,
  },
};

const BUDGETS = ["Under $500/mo", "$500–$1,000/mo", "$1,000–$2,500/mo", "$2,500–$5,000/mo", "$5,000+/mo", "Not sure yet"];

const EMPTY = { name: "", email: "", phone: "", company: "", website: "", budget: "", biztype: "", city: "", state_abbr: "", zip: "", timeframe: "", notes: "" };

export default function ServiceLanding({ vertical = "business" }) {
  const c = LANDINGS[vertical] || LANDINGS.business;
  const [form, setForm] = useState(EMPTY);
  const [needs, setNeeds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleNeed = (k) => setNeeds((n) => (n.includes(k) ? n.filter((x) => x !== k) : [...n, k]));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.email.trim()) return toast.error("Please enter your email");
    setBusy(true);
    try {
      await api.post("/service-lead", { ...form, vertical, needs });
      setDone(true);
      toast.success("Thanks! We'll be in touch shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12" data-testid={`service-${vertical}-page`}>
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-crimson">
            <c.icon className="h-4 w-4" />{c.eyebrow}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-sam-navy mt-3 leading-[1.08]">{c.h1}</h1>
          <p className="text-sam-slate mt-4 text-base">{c.sub}</p>

          <div className="grid sm:grid-cols-2 gap-3 mt-8">
            {c.benefits.map((b) => (
              <div key={b.label} className="rounded-xl border border-border bg-card p-4" data-testid={`benefit-${b.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="flex items-center gap-2 font-display font-bold text-sam-navy"><b.icon className="h-4 w-4 text-sam-crimson" />{b.label}</div>
                <p className="text-xs text-sam-slate mt-1">{b.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          {done ? (
            <div className="text-center py-8" data-testid="service-success">
              <CheckCircle2 className="h-12 w-12 text-sam-green mx-auto mb-3" />
              <h2 className="font-display text-xl font-bold text-sam-navy">You're all set!</h2>
              <p className="text-sam-slate text-sm mt-1">A Saving America Money specialist will reach out to help you get more customers.</p>
              <Link to="/" className="inline-block mt-5"><Button variant="outline" data-testid="service-home-btn">Back to home</Button></Link>
            </div>
          ) : (
            <>
              <h2 className="font-display text-xl font-bold text-sam-navy">{c.cta}</h2>
              <p className="text-sm text-sam-slate mb-4">Tell us about your business — no obligation.</p>
              <div className="space-y-3">
                <div><Label className="text-xs">Full name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="svc-name" placeholder="Jane Smith" /></div>
                {c.showCompany && (
                  <div><Label className="text-xs">{c.companyLabel}</Label><Input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="svc-company" placeholder="Acme Services LLC" /></div>
                )}
                {c.showBizType && (
                  <div><Label className="text-xs">What does your business do?</Label><Input value={form.biztype} onChange={(e) => set("biztype", e.target.value)} data-testid="svc-biztype" placeholder="Real estate agent, roofer, boutique…" /></div>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="svc-email" placeholder="you@email.com" /></div>
                  <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="svc-phone" placeholder="(555) 555-5555" /></div>
                </div>
                {c.showWebsite && (
                  <div><Label className="text-xs">Website (optional)</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="svc-website" placeholder="www.yourbusiness.com" /></div>
                )}
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label className="text-xs">City</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="svc-city" placeholder="Monett" /></div>
                  <div><Label className="text-xs">State</Label><Input value={form.state_abbr} onChange={(e) => set("state_abbr", e.target.value.toUpperCase())} data-testid="svc-state" placeholder="MO" maxLength={2} /></div>
                  <div><Label className="text-xs">ZIP</Label><Input value={form.zip} onChange={(e) => set("zip", e.target.value)} data-testid="svc-zip" placeholder="65708" /></div>
                </div>
                <div>
                  <Label className="text-xs">{c.timeLabel}</Label>
                  <Select value={form.timeframe} onValueChange={(v) => set("timeframe", v)}>
                    <SelectTrigger data-testid="svc-timeframe"><SelectValue placeholder="Choose a timeframe" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ASAP">As soon as possible</SelectItem>
                      <SelectItem value="1-3 months">1–3 months</SelectItem>
                      <SelectItem value="3-6 months">3–6 months</SelectItem>
                      <SelectItem value="Just researching">Just researching</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {c.showBudget && (
                  <div>
                    <Label className="text-xs">Monthly marketing budget</Label>
                    <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                      <SelectTrigger data-testid="svc-budget"><SelectValue placeholder="Choose a range" /></SelectTrigger>
                      <SelectContent>
                        {BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs">{c.needsLabel}</Label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {c.needs.map((n) => (
                      <button type="button" key={n} onClick={() => toggleNeed(n)} data-testid={`svc-need-${n.toLowerCase().replace(/\s|&/g, "-")}`}
                        className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${needs.includes(n) ? "bg-sam-crimson text-white border-sam-crimson" : "bg-white text-sam-navy border-border hover:border-sam-crimson/40"}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div><Label className="text-xs">Anything else? (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="svc-notes" placeholder="Tell us more about your goals…" /></div>
                <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" onClick={submit} disabled={busy} data-testid="svc-submit-btn">
                  {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : c.cta}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <SocialProof />
    </div>
  );
}
